<!--
	Stateful container for the in-app support form. Wraps Modal, mounts the
	isolated Halo embed iframe (static/halo-form.html), and owns its load state.

	Readiness model: the iframe page posts one of two outcomes — `halo-form-ready`
	(fields rendered → reveal) or `halo-form-error` (an embed asset failed, or no
	fields rendered → error state with a portal fallback). While loading we show a
	non-blocking "open the portal" hint after SUPPORT_SLOW_HINT_MS, and give up to
	the error state after a hard SUPPORT_LOAD_TIMEOUT_MS ceiling so a stalled or very
	slow embed never spins forever. A browser offline/error page (fires `onload` but
	never posts `halo-form-loaded`) fails fast at the handshake grace.
-->
<script lang="ts">
	import { getLogger } from '@logtape/logtape';
	import Modal from './Modal.svelte';
	import SupportForm from './SupportForm.svelte';
	import { haloFallbackLink, haloEmbedConfig, environment } from '#lib/config.js';
	import { appStore } from '#lib/stores/appStore.svelte.js';
	import * as Sentry from '@sentry/sveltekit';
	import {
		buildHaloFormSrc,
		buildHaloFallbackHref,
		parseHaloMessage,
		MAX_SUPPORT_RETRIES,
		SUPPORT_ONLOAD_GRACE_MS,
		SUPPORT_SLOW_HINT_MS,
		SUPPORT_LOAD_TIMEOUT_MS
	} from './supportForm';

	interface Props {
		open: boolean;
		onclose: () => void;
		/** Warm the embed ahead of opening (intent-based preload). Sticky once true. */
		warm?: boolean;
	}

	const { open, onclose, warm = false }: Props = $props();

	const log = getLogger(['platform', 'support']);

	type Status = 'loading' | 'ready' | 'error';
	let status = $state<Status>('loading');
	let slow = $state(false);
	let retry = $state(0);
	let formHeight = $state<number | null>(null);
	let revealTimer: ReturnType<typeof setTimeout> | undefined;
	let hintTimer: ReturnType<typeof setTimeout> | undefined;
	let loadTimer: ReturnType<typeof setTimeout> | undefined;
	// Whether the iframe loaded OUR page (heard its handshake) vs. a browser
	// offline/error page (which still fires the iframe's `onload`).
	let pageLoaded = false;

	// Prefill the hidden SafeSystem fields with the active app, environment, and host URL; an empty application (no active app) clears Halo's default rather than sending a stale one.
	const supportPrefill = $derived({
		environment: environment ? environment[0].toUpperCase() + environment.slice(1) : '',
		application: appStore.app?.name ?? '',
		hostUrl: typeof window !== 'undefined' ? window.location.origin : ''
	});
	const src = $derived(buildHaloFormSrc(retry, haloEmbedConfig, supportPrefill));
	// Portal fallback gets the same values as `customfield_NNN=` params (the portal prepopulates from them).
	const supportFallbackHref = $derived(buildHaloFallbackHref(haloFallbackLink, supportPrefill));
	// Active = warming in the background or open. Driving the lifecycle off this
	// (not just `open`) lets the embed preload on intent and be ready by open.
	const active = $derived(warm || open);

	function clearTimers() {
		clearTimeout(revealTimer);
		clearTimeout(hintTimer);
		clearTimeout(loadTimer);
		revealTimer = undefined;
		hintTimer = undefined;
		loadTimer = undefined;
	}

	/** Clear the loading mask once the iframe signals the form rendered. */
	function reveal() {
		if (status !== 'loading') return;
		status = 'ready';
		slow = false;
		clearTimers();
	}

	function handleMessage(event: MessageEvent) {
		const msg = parseHaloMessage(event, window.location.origin);
		if (msg?.type === 'ready') reveal();
		else if (msg?.type === 'loaded') pageLoaded = true;
		else if (msg?.type === 'resize') formHeight = msg.height;
		// A page that rendered no form fields won't recover on reload, so skip retries for it.
		else if (msg?.type === 'error')
			handleIframeError(
				msg.resource === 'form'
					? 'embed rendered no form fields'
					: `embed ${msg.resource} failed to load`,
				msg.resource !== 'form'
			);
	}

	/**
	 * The iframe fired `onload`. If our page's handshake never arrives within the
	 * grace, the iframe is showing a browser offline/error page — surface the error
	 * instead of a blank box. If it did load, we wait for the form's ready/error
	 * signal (the iframe always posts one), so the slow-load hint can show and a
	 * no-fields render becomes an error rather than a premature blank reveal.
	 */
	function handleIframeLoad() {
		if (status !== 'loading') return;
		clearTimeout(revealTimer);
		revealTimer = setTimeout(() => {
			if (!pageLoaded) handleIframeError('page did not load', false);
		}, SUPPORT_ONLOAD_GRACE_MS);
	}

	/** A genuine load failure (page or a Halo embed asset): retry, then show the error state. */
	function handleIframeError(reason = 'iframe failed to load', retryable = true) {
		// Only act while loading — ignore late messages/timers once revealed or errored
		// (don't tear down a rendered form or double-report the same failure).
		if (status !== 'loading') return;
		clearTimeout(revealTimer);
		// Retry transient embed-asset failures (e.g. a Halo CDN blip); but a failed
		// *page* load (offline / server down) won't fix on an immediate reload, so go
		// straight to the error state — the "Try again" button drives recovery.
		if (retryable && retry < MAX_SUPPORT_RETRIES) {
			retry += 1;
			status = 'loading';
			// Fresh load attempt — clear the previous attempt's transient state so the
			// retry doesn't inherit a stale slow-hint or an outdated reported height.
			pageLoaded = false;
			slow = false;
			formHeight = null;
			log.warn('Halo support form load failed ({reason}), retrying {retry}', { reason, retry });
			return;
		}
		status = 'error';
		clearTimers();
		log.error('Halo support form failed to load ({reason})', { reason });
		// No-op unless hooks.client.ts initialised Sentry.
		Sentry.captureException(new Error('Halo support form failed to load'), {
			tags: { feature: 'support-form' },
			extra: { retries: retry, reason }
		});
	}

	function reset() {
		status = 'loading';
		slow = false;
		retry = 0;
		formHeight = null;
		pageLoaded = false;
	}

	function armTimers() {
		clearTimeout(hintTimer);
		clearTimeout(loadTimer);
		// Non-blocking nudge while the load may still complete.
		hintTimer = setTimeout(() => {
			if (status === 'loading') slow = true;
		}, SUPPORT_SLOW_HINT_MS);
		// Hard ceiling: give up to the error state so a stalled/slow embed can't
		// spin forever. The error state's "Try again" arms a fresh attempt.
		loadTimer = setTimeout(() => {
			if (status === 'loading') handleIframeError('load timed out', false);
		}, SUPPORT_LOAD_TIMEOUT_MS);
	}

	function retryNow() {
		reset();
		armTimers();
	}

	$effect(() => {
		if (!active) return;
		// Run the load lifecycle once the form becomes active (warmed or opened).
		// A warmed form stays active (sticky) so it isn't reset/reloaded on close.
		reset();
		window.addEventListener('message', handleMessage);
		armTimers();
		return () => {
			window.removeEventListener('message', handleMessage);
			clearTimers();
		};
	});
</script>

<Modal {open} title="Contact support" {onclose} size="lg">
	<!-- Mount when warmed or open. Modal renders its children eagerly, so gating on
	     `active` keeps the 10MB Halo embed from loading at app boot — it loads only
	     on intent (hover) or open. While warmed-but-closed the dialog is hidden, so
	     the embed preloads in the background and is ready by the time it's shown. -->
	{#if active}
		<SupportForm
			{status}
			{src}
			{slow}
			height={formHeight}
			fallbackHref={supportFallbackHref}
			onload={handleIframeLoad}
			onerror={() => handleIframeError('iframe failed to load', false)}
			onretry={status === 'error' ? retryNow : undefined} />
	{/if}
</Modal>
